import { useMerchantReturnPolicy } from '../hooks/useReturnsPolicy';

/**
 * TCS-admin READ-ONLY view of a merchant's return/exchange policy (PE-2163).
 *
 * Read-only on purpose: watchman exposes an admin-by-id READ
 * (`GET /merchants/:merchantId/store-config`) but no admin-by-id WRITE for these
 * fields — the only write is the merchant's own `PATCH /merchants/me/store-config`,
 * which resolves the merchant from the JWT. Unlike VAT, which has a
 * `PATCH :merchantId/vat-config` counterpart, there is no
 * `PATCH :merchantId/return-policy`, so TCS staff cannot edit on a merchant's
 * behalf yet. Tracked as a backend ask; make this editable once that route lands.
 */
export function MerchantReturnPolicyPanel({ merchantId }: { merchantId: string }) {
  const { data, isLoading, isError } = useMerchantReturnPolicy(merchantId);

  const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex items-start justify-between py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground pr-4">{label}</span>
      <span className="text-sm font-medium text-foreground text-right">{value}</span>
    </div>
  );

  const days = (n: number | null | undefined) => (n == null ? 'No limit' : `${n} day${n === 1 ? '' : 's'}`);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-foreground">Returns &amp; Exchanges</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Set by the merchant from their own dashboard — read-only here.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading policy…</p>
      ) : isError || !data ? (
        <p className="text-sm text-muted-foreground">Could not load this merchant's returns policy.</p>
      ) : !data.returnsEnabled ? (
        <p className="text-sm text-foreground">
          This merchant does not accept returns or exchanges.
        </p>
      ) : (
        <div>
          <Row label="Request window (after delivery)" value={days(data.returnWindowDays)} />
          <Row label="Return-shipment window (after approval)" value={days(data.returnShipmentWindowDays)} />
          <Row
            label="Ineligible categories"
            value={data.ineligibleCategoryIds?.length ? `${data.ineligibleCategoryIds.length} category(ies)` : 'None'}
          />
          <Row
            label="Restocking fee"
            value={
              data.restockingFeeEnabled && data.restockingFeePercentage != null
                ? `${data.restockingFeePercentage}%`
                : 'None'
            }
          />
          <Row
            label="Restock original item"
            value={data.restockInventoryTrigger === 'ON_APPROVAL' ? 'On approval' : 'On receipt'}
          />
          <Row
            label="Return shipping paid by"
            value={data.returnShippingCostAbsorbedByMerchant ? 'Merchant' : 'Customer'}
          />
          {data.returnPolicy && <Row label="Storefront policy text" value={data.returnPolicy} />}
        </div>
      )}
    </div>
  );
}
